// Mailgun and Resend, as this service can see them.
//
// Sign-in and Ride codes leave through Mailgun. novaexhibits.com mail leaves
// through Resend. The numbers are the providers'. A missing total is a missing
// total, not a zero.

import { useState, type ReactNode } from "react";
import { DefList, NoticeBox } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { readEmailStats, type EmailProviderReport, type EmailStatsReport } from "../api/admin.js";
import type { ApiOptions } from "../api/client.js";
import type { SessionToken } from "../session/store.js";

export type EmailStatsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
};

type Phase =
  | { readonly kind: "idle" }
  | { readonly kind: "reading" }
  | { readonly kind: "ready"; readonly stats: EmailStatsReport }
  | { readonly kind: "refused"; readonly detail: string };

export function EmailStats({ api, token, onSessionEnded }: EmailStatsProps): ReactNode {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const load = async (): Promise<void> => {
    setPhase({ kind: "reading" });
    const result = await readEmailStats(api, token);
    if (result.ok) {
      setPhase({ kind: "ready", stats: result.value });
      return;
    }
    if (result.kind === "session_ended") {
      onSessionEnded(result.detail);
      return;
    }
    setPhase({ kind: "refused", detail: result.detail });
  };

  return (
    <section>
      <SecLabel info="Mailgun sends sign-in and Ride codes. Resend sends novaexhibits.com mail from Functions.">
        Sending
      </SecLabel>
      {phase.kind === "idle" ? (
        <div className="panel panel-stack">
          <p>
            Mailgun sends sign-in and Ride codes. Resend sends novaexhibits.com mail. The numbers are
            the providers&rsquo;.
          </p>
          <div className="acts acts-flush">
            <button type="button" className="btn" onClick={() => void load()}>
              Show Mailgun and Resend
            </button>
          </div>
        </div>
      ) : null}
      {phase.kind === "reading" ? <p className="help">Reading Mailgun and Resend.</p> : null}
      {phase.kind === "refused" ? (
        <div className="panel panel-stack">
          <NoticeBox kind="amber" title="Sending numbers are not available">
            <p>{phase.detail}</p>
          </NoticeBox>
          <div className="acts acts-flush">
            <button type="button" className="btn" onClick={() => void load()}>
              Try Mailgun and Resend again
            </button>
          </div>
        </div>
      ) : null}
      {phase.kind === "ready" ? (
        <div className="panel panel-stack">
          <ProviderBlock report={phase.stats.mailgun} />
          <ProviderBlock report={phase.stats.resend} />
        </div>
      ) : null}
    </section>
  );
}

function ProviderBlock({ report }: { readonly report: EmailProviderReport }): ReactNode {
  const title = report.provider === "mailgun" ? "Mailgun" : "Resend";
  return (
    <div className="stack">
      <h3 className="panel-h">{title}</h3>
      {report.domain === null ? null : <p className="help">{report.domain}</p>}
      <p className="help">{report.window}</p>
      {report.configured ? (
        <DefList
          rows={[
            ["Accepted", count(report.accepted)],
            ["Delivered", count(report.delivered)],
            ["Failed", count(report.failed)],
            ["Opened", count(report.opened)],
            ["Complained", count(report.complained)],
          ]}
        />
      ) : null}
      {report.note === null ? null : <p className="help">{report.note}</p>}
      {report.recent.length === 0 ? null : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Event</th>
                <th scope="col">To</th>
              </tr>
            </thead>
            <tbody>
              {report.recent.map((row, index) => (
                <tr key={`${row.at}-${row.event}-${index}`}>
                  <td data-label="When">{row.at === "" ? "—" : new Date(row.at).toLocaleString()}</td>
                  <td data-label="Event">{row.event}</td>
                  <td data-label="To">{row.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function count(value: number | null): string {
  return value === null ? "not reported" : String(value);
}
